import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  ArrowUpDown,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  Eye,
  FileText,
  Folder,
  Lock,
  LockOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Trash2,
  UserRound,
  X,
  LucideIcon,
} from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { wikiService } from '../../services/wikiService';
import { LockMarker } from '../../features/confidentiality';
import { WikiPage } from '../../types/wiki';
import { EntityTreeSidebar, TreeSelection } from '../../components/library/EntityTreeSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { asNumber, asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  ENTITY_TONE,
  EntityIcon,
  IconButton,
  Menu,
  Page,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Skeleton,
} from '../../components/ui';
import { timeAgo, formatDateTime, WIKI_STATUS } from '../../components/wiki/wikiFormat';

type StatusFilter = '' | 'approved' | 'pending' | 'draft' | 'rejected';
type SortOption = 'newest' | 'oldest' | 'az' | 'za' | 'updated';
type StatusTab = 'all' | Exclude<StatusFilter, ''>;

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Published' },
  { value: 'pending', label: 'Review' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'az', label: 'A–Z' },
  { value: 'za', label: 'Z–A' },
];

const PAGE_SIZES = [12, 24, 48];

/* One codec per parameter, made once: the URL state reads them on every render */
const SCOPE = asOneOf<TreeSelection['type']>([
  'all',
  'stakeholder',
  'organization',
  'stakeholders',
  'organizations',
  'unassigned',
]);
const STATUS = asOneOf<StatusFilter>(['', 'approved', 'pending', 'draft', 'rejected']);
const SORT = asOneOf(SORT_OPTIONS.map(option => option.value));
const PAGE = asNumber(1);
/** The page sizes the picker offers; anything else falls back to the default */
const PAGE_SIZE = asNumber(1, PAGE_SIZES[PAGE_SIZES.length - 1]);

/** Empty icon slot so menu labels stay aligned next to the checked option */
const NoIcon = (({ className }: { className?: string }) => <span className={className} aria-hidden="true" />) as unknown as LucideIcon;

function sortToParams(sort: SortOption): { sortBy: string; sortOrder: 'ASC' | 'DESC' } {
  switch (sort) {
    case 'newest': return { sortBy: 'createdAt', sortOrder: 'DESC' };
    case 'oldest': return { sortBy: 'createdAt', sortOrder: 'ASC' };
    case 'updated': return { sortBy: 'updatedAt', sortOrder: 'DESC' };
    case 'az': return { sortBy: 'title', sortOrder: 'ASC' };
    case 'za': return { sortBy: 'title', sortOrder: 'DESC' };
  }
}

function selectionLabel(selection: TreeSelection, name: string): string {
  switch (selection.type) {
    case 'stakeholders': return 'Stakeholders';
    case 'organizations': return 'Organizations';
    case 'stakeholder':
    case 'organization': return name || 'Selected';
    default: return 'All pages';
  }
}

export const WikiHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  // Which shelf of the library, and how it is filtered, is in the address bar:
  // a link hands someone else the same list, and coming back finds it again
  const write = useUrlWriter();
  const [scope] = useUrlState<TreeSelection['type']>('scope', 'all', SCOPE);
  const [scopeId] = useUrlState('scopeId', '');
  const selection = useMemo<TreeSelection>(
    () => ({ type: scope, id: scopeId || undefined }),
    [scope, scopeId],
  );

  /** Narrowing what is listed starts again at page one, in one move */
  const refilter = useCallback(
    (changes: Record<string, string | null>) => write({ ...changes, page: null }),
    [write],
  );

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [scopeSheetOpen, setScopeSheetOpen] = useState(false);

  const [pages, setPages] = useState<WikiPage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q] = useUrlState('q', '');
  const [statusFilter] = useUrlState<StatusFilter>('status', '', STATUS);
  const [sort] = useUrlState<SortOption>('sort', 'newest', SORT);
  const [page, setPage] = useUrlState('page', 1, PAGE);
  const [limit] = useUrlState('limit', PAGE_SIZES[0], PAGE_SIZE);

  // The box answers every keystroke; the URL catches up once typing stops
  const [searchInput, setSearchInput] = useState(q);
  const settledSearch = useDebounce(searchInput, 300);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [deleteTarget, setDeleteTarget] = useState<WikiPage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewPage, setPreviewPage] = useState<WikiPage | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const [entityCounts, setEntityCounts] = useState<Record<string, number>>({});
  const [totalWikiCount, setTotalWikiCount] = useState(0);

  // Load entity counts via lightweight endpoint
  const loadCounts = useCallback(() => {
    wikiService.getCounts().then(res => {
      setEntityCounts(res.counts || {});
      setTotalWikiCount(res.total || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { sortBy, sortOrder } = sortToParams(sort);

      const params: any = {
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter || undefined,
        search: q || undefined,
      };

      if (selection.type === 'stakeholder' && selection.id) {
        params.entityType = 'stakeholder';
        params.entityId = selection.id;
      } else if (selection.type === 'organization' && selection.id) {
        params.entityType = 'organization';
        params.entityId = selection.id;
      }

      const resp = await wikiService.getWikiPages(params);
      let data = resp.data || [];

      // Client-side filter for entity-type groups
      if (selection.type === 'stakeholders') {
        data = data.filter(p => p.entityType === 'stakeholder');
      } else if (selection.type === 'organizations') {
        data = data.filter(p => p.entityType === 'organization');
      }

      setPages(data);
      setTotal(selection.type === 'stakeholders' || selection.type === 'organizations' ? data.length : resp.total || 0);
    } catch {
      setError('Failed to load wiki pages');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, statusFilter, q, selection]);

  useEffect(() => { loadPages(); }, [loadPages]);

  useEffect(() => {
    if (settledSearch !== q) refilter({ q: settledSearch || null });
  }, [settledSearch, q, refilter]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await wikiService.deleteWikiPage(deleteTarget.id);
      setPages(prev => prev.filter(p => p.id !== deleteTarget.id));
      setTotal(prev => prev - 1);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      setActionMessage('Page deleted');
      setTimeout(() => setActionMessage(null), 3000);
      loadCounts();
    } catch (err: any) {
      setActionMessage(err?.response?.data?.message || 'Failed to delete page');
      setTimeout(() => setActionMessage(null), 4000);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, loadCounts]);

  const handleToggleLock = useCallback(async (p: WikiPage) => {
    try {
      const updated = await wikiService.toggleLock(p.id, !p.isLocked);
      setPages(prev => prev.map(pg => pg.id === p.id ? { ...pg, isLocked: updated.isLocked } : pg));
      setActionMessage(updated.isLocked ? 'Page locked' : 'Page unlocked');
      setTimeout(() => setActionMessage(null), 2000);
    } catch {
      setActionMessage('Failed to toggle lock');
      setTimeout(() => setActionMessage(null), 3000);
    }
  }, []);

  const handleBulkDelete = useCallback(async () => {
    const toDelete = pages.filter(p => selectedIds.has(p.id) && !p.isLocked);
    for (const p of toDelete) {
      try { await wikiService.deleteWikiPage(p.id); } catch {}
    }
    setSelectedIds(new Set());
    loadPages();
    loadCounts();
    setActionMessage(`Deleted ${toDelete.length} page(s)`);
    setTimeout(() => setActionMessage(null), 3000);
  }, [selectedIds, pages, loadPages, loadCounts]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pages.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pages.map(p => p.id)));
  };

  /** Names the tree hands over, kept so the breadcrumb can read them back */
  const labels = useRef<Record<string, string>>({});

  const selectScope = (s: TreeSelection) => {
    if (s.id && s.name) labels.current[s.id] = s.name;
    refilter({ scope: s.type === 'all' ? null : s.type, scopeId: s.id ?? null });
    setSelectedIds(new Set());
  };

  /**
   * A name is a label, not view state, so the URL carries only the id and the
   * name is read back from the pages that belong to the entity. One seen once
   * is remembered, so a filter that empties the list — or an id that matches
   * nothing at all — still reads as the shelf you opened.
   */
  const scopeName = useMemo(() => {
    if (!scopeId) return '';
    const name = pages.find(p => p.entityId === scopeId)?.entityName || '';
    if (name) labels.current[scopeId] = name;
    return name || labels.current[scopeId] || '';
  }, [pages, scopeId]);

  const breadcrumb = useMemo(() => {
    const parts: { label: string; onClick?: () => void }[] = [];
    if (selection.type === 'stakeholders') parts.push({ label: 'Stakeholders' });
    else if (selection.type === 'organizations') parts.push({ label: 'Organizations' });
    else if (selection.type === 'stakeholder' && scopeName) {
      parts.push({ label: 'Stakeholders', onClick: () => selectScope({ type: 'stakeholders' }) });
      parts.push({ label: scopeName });
    } else if (selection.type === 'organization' && scopeName) {
      parts.push({ label: 'Organizations', onClick: () => selectScope({ type: 'organizations' }) });
      parts.push({ label: scopeName });
    }
    return parts;
    // selectScope is made afresh each render; the crumbs follow where we are
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, scopeName]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // A page number past the end — an old link, or pages deleted since — lists
  // nothing and hides the pager with it, so it falls back to the first page
  useEffect(() => {
    if (!loading && page > totalPages) setPage(1);
  }, [loading, page, totalPages, setPage]);

  const filtered = !!(q || statusFilter);
  const scoped = selection.type !== 'all';
  const showSelection = !!isAdmin;

  const sanitized = useMemo(() => {
    if (!previewPage?.content) return '';
    return DOMPurify.sanitize(previewPage.content);
  }, [previewPage]);

  const meta = (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5">
      {/* Phones show the scope on the Library button instead */}
      {scoped && (
        <span className="hidden min-w-0 flex-wrap items-center gap-x-1.5 lg:inline-flex">
          <button type="button" onClick={() => selectScope({ type: 'all' })} className="transition-colors hover:text-ink">
            Wiki
          </button>
          {breadcrumb.map((part, i) => (
            <React.Fragment key={i}>
              <ChevronRight className="h-3 w-3 text-neutral-300" strokeWidth={1.75} aria-hidden="true" />
              {part.onClick ? (
                <button type="button" onClick={part.onClick} className="transition-colors hover:text-ink">{part.label}</button>
              ) : (
                <span className="max-w-[220px] truncate text-ink">{part.label}</span>
              )}
            </React.Fragment>
          ))}
          <span className="text-neutral-300">·</span>
        </span>
      )}
      <span className="tabular-nums">{loading ? '…' : `${total} ${total === 1 ? 'page' : 'pages'}`}</span>
    </span>
  );

  const sortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label;

  return (
    <DashboardLayout>
      {/* On desktop the library tree is a quiet column flush with the app sidebar */}
      <div className="lg:-m-6 lg:flex">
        {sidebarOpen && (
          <aside className="hidden flex-shrink-0 lg:sticky lg:top-16 lg:block lg:h-[calc(100vh-64px)] lg:w-[248px]">
            <EntityTreeSidebar
              mode="wiki"
              variant="column"
              selection={selection}
              onSelect={selectScope}
              entityCounts={entityCounts}
              totalCount={totalWikiCount}
            />
          </aside>
        )}

        <div className="min-w-0 flex-1 lg:p-6">
          <Page>
            <PageHeader
              title="Wiki"
              meta={meta}
              actions={
                <>
                  <IconButton
                    label={sidebarOpen ? 'Hide library' : 'Show library'}
                    size="sm"
                    className="hidden lg:inline-flex"
                    onClick={() => setSidebarOpen(p => !p)}
                  >
                    {sidebarOpen ? <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} /> : <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />}
                  </IconButton>
                  <Button variant="primary" size="sm" onClick={() => navigate('/wiki/new')}>
                    <Plus className="h-4 w-4" strokeWidth={2} />
                    New
                  </Button>
                </>
              }
            />

            {/* Toolbar: on phones search + library + sort, then the status filter on its own row */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <SearchInput
                className="min-w-0 flex-1 sm:w-64 sm:flex-none"
                placeholder="Search"
                aria-label="Search pages"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                className="h-10 min-w-0 max-w-[40%] flex-shrink-0 sm:h-9 lg:hidden"
                onClick={() => setScopeSheetOpen(true)}
                aria-haspopup="dialog"
                aria-label={`Library: ${selectionLabel(selection, scopeName)}`}
              >
                <Folder className={`h-4 w-4 flex-shrink-0 ${scoped ? 'text-primary-600' : 'text-neutral-400'}`} strokeWidth={1.75} />
                <span className="truncate">{scoped ? selectionLabel(selection, scopeName) : 'Library'}</span>
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
              </Button>
              <SegmentedControl
                label="Status"
                className="order-last w-full [&>button]:flex-1 sm:order-none sm:w-auto sm:[&>button]:flex-none"
                items={STATUS_TABS}
                value={(statusFilter || 'all') as StatusTab}
                onChange={v => refilter({ status: v === 'all' ? null : v })}
              />
              <Menu
                className="flex-shrink-0 sm:order-last sm:ml-auto"
                items={SORT_OPTIONS.map(o => ({
                  label: o.label,
                  icon: o.value === sort ? Check : NoIcon,
                  onSelect: () => refilter({ sort: o.value === 'newest' ? null : o.value }),
                }))}
                trigger={props => (
                  <Button variant="ghost" size="sm" className="h-10 px-2.5 sm:h-9 sm:px-3" aria-label={`Sort: ${sortLabel}`} {...props}>
                    <ArrowUpDown className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
                    <span className="hidden sm:inline">{sortLabel}</span>
                  </Button>
                )}
              />
            </div>

            {error ? (
              <p role="alert" className="flex items-center justify-between gap-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
                {error}
                <button type="button" onClick={loadPages} className="font-medium underline-offset-2 hover:underline">Retry</button>
              </p>
            ) : (
              <Card>
                {/* Column labels (desktop) */}
                <div className="hidden h-9 items-center gap-3 border-b border-neutral-200/70 px-4 text-[12px] text-neutral-400 sm:flex">
                  {showSelection && (
                    <Checkbox
                      aria-label="Select all"
                      checked={selectedIds.size === pages.length && pages.length > 0}
                      onChange={toggleSelectAll}
                      disabled={loading || pages.length === 0}
                    />
                  )}
                  <span className="w-4" aria-hidden="true" />
                  <span className="flex-1">Title</span>
                  <span className="w-44 lg:w-52">Belongs to</span>
                  <span className="w-20 text-right">Updated</span>
                  <span className="w-8" aria-hidden="true" />
                </div>

                {loading ? (
                  <ul aria-hidden="true" className="divide-y divide-neutral-200/70">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <li key={i} className="flex h-[60px] items-center gap-3 px-4 sm:h-11">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className={`h-4 ${i % 2 ? 'w-1/3' : 'w-1/2'}`} />
                        <Skeleton className="ml-auto hidden h-4 w-28 sm:block" />
                      </li>
                    ))}
                  </ul>
                ) : pages.length === 0 ? (
                  <EmptyState
                    icon={FileText}
                    title={filtered ? 'No matches' : 'No pages yet'}
                    action={
                      !filtered && (
                        <Button variant="secondary" size="sm" onClick={() => navigate('/wiki/new')}>
                          <Plus className="h-4 w-4" strokeWidth={2} />
                          New page
                        </Button>
                      )
                    }
                  />
                ) : (
                  <ul className="divide-y divide-neutral-200/70">
                    {pages.map(p => {
                      const status = WIKI_STATUS[p.status] || WIKI_STATUS.draft;
                      const selected = selectedIds.has(p.id);
                      const kind = p.entityType === 'stakeholder' ? 'stakeholder' : 'organization';
                      const KindIcon = kind === 'stakeholder' ? UserRound : Building2;
                      const entity = p.entityName || (p.entityType === 'stakeholder' ? 'Stakeholder' : 'Organization');
                      const author = p.creator?.firstName ? `${p.creator.firstName} ${p.creator.lastName || ''}`.trim() : '';
                      return (
                        <li
                          key={p.id}
                          className={`group flex items-center gap-3 pl-4 pr-2 transition-colors first:rounded-t-[15px] last:rounded-b-[15px] hover:bg-neutral-50 sm:pr-3 sm:first:rounded-t-none ${
                            selected ? 'bg-neutral-50' : ''
                          }`}
                        >
                          {showSelection && (
                            <Checkbox
                              className="hidden sm:inline-flex"
                              aria-label={`Select ${p.title}`}
                              checked={selected}
                              onChange={() => toggleSelect(p.id)}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/wiki/${p.id}`)}
                            title={author ? `${p.title} · by ${author}` : p.title}
                            className="flex min-h-[60px] min-w-0 flex-1 items-center gap-3 py-2 text-left focus-visible:outline-none sm:min-h-[44px] sm:py-0"
                          >
                            <FileText className="hidden h-4 w-4 flex-shrink-0 text-neutral-400 sm:block" strokeWidth={1.75} />
                            <span className="min-w-0 flex-1">
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="truncate text-[15px] text-ink sm:text-[13px]">{p.title}</span>
                                {p.isLocked && <Lock className="h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={2} aria-label="Locked" />}
                                <LockMarker record={p} />
                              </span>
                              <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-neutral-400 sm:hidden">
                                <KindIcon className={`h-3 w-3 flex-shrink-0 ${ENTITY_TONE[kind].text}`} strokeWidth={2} aria-hidden="true" />
                                <span className="truncate">{entity}</span>
                                <span aria-hidden="true">·</span>
                                <span className="flex-shrink-0 tabular-nums">{timeAgo(p.updatedAt)}</span>
                              </span>
                            </span>
                            {p.status !== 'approved' && <Pill tone={status.tone}>{status.label}</Pill>}
                            <span className="hidden w-44 min-w-0 items-center gap-2 text-[13px] text-neutral-500 sm:flex lg:w-52">
                              <EntityIcon kind={kind} size="xs" />
                              <span className="truncate">{entity}</span>
                            </span>
                            <span
                              className="hidden w-20 text-right text-[12px] tabular-nums text-neutral-400 sm:block"
                              title={formatDateTime(p.updatedAt)}
                            >
                              {timeAgo(p.updatedAt)}
                            </span>
                          </button>
                          <Menu
                            items={[
                              { label: 'Preview', icon: Eye, onSelect: () => setPreviewPage(p) },
                              { label: 'Edit', icon: Pencil, onSelect: () => navigate(`/wiki/${p.id}/edit`) },
                              ...(isAdmin
                                ? [
                                    { label: p.isLocked ? 'Unlock' : 'Lock', icon: p.isLocked ? LockOpen : Lock, onSelect: () => handleToggleLock(p) },
                                    { label: p.isLocked ? 'Locked' : 'Delete', icon: Trash2, danger: true, disabled: p.isLocked, onSelect: () => setDeleteTarget(p) },
                                  ]
                                : []),
                            ]}
                            trigger={props => (
                              <IconButton label={`Actions for ${p.title}`} size="sm" className="text-neutral-400" {...props}>
                                <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                              </IconButton>
                            )}
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            )}

            {/* Pagination */}
            {!loading && !error && pages.length > 0 && (total > PAGE_SIZES[0] || limit !== PAGE_SIZES[0]) && (
              <div className="mt-3 flex items-center justify-between gap-3 text-[12px] text-neutral-400">
                <span className="tabular-nums">
                  {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </span>
                <div className="flex items-center gap-1">
                  <label className="relative mr-1 inline-flex items-center">
                    <span className="sr-only">Per page</span>
                    <select
                      value={limit}
                      onChange={e => refilter({ limit: Number(e.target.value) === PAGE_SIZES[0] ? null : e.target.value })}
                      className="h-8 appearance-none rounded-full border-0 bg-transparent bg-none py-0 pl-3 pr-7 text-[16px] text-neutral-500 ring-1 ring-inset ring-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600/30 sm:text-[12px]"
                    >
                      {PAGE_SIZES.map(size => <option key={size} value={size}>{size} / page</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5" strokeWidth={1.75} />
                  </label>
                  <IconButton label="Previous page" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                  <span className="px-1 tabular-nums">{page} / {totalPages}</span>
                  <IconButton label="Next page" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                </div>
              </div>
            )}
          </Page>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && isAdmin && (
        <div className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 pb-4" style={{ bottom: 'var(--app-tabbar-h, 0px)' }}>
          <div className="pointer-events-auto flex animate-fade-up items-center gap-1 rounded-full bg-ink py-1.5 pl-4 pr-1.5 font-ui text-[13px] text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.35)]">
            <span className="mr-2 tabular-nums">{selectedIds.size} selected</span>
            <Button variant="primary" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
              Delete
            </Button>
            <IconButton label="Clear selection" size="sm" className="text-neutral-400 hover:bg-white/10 hover:text-white" onClick={() => setSelectedIds(new Set())}>
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
        </div>
      )}

      {/* Feedback */}
      {actionMessage && (
        <div
          className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4"
          style={{ bottom: `calc(var(--app-tabbar-h, 0px) + ${selectedIds.size > 0 && isAdmin ? 76 : 16}px)` }}
        >
          <p role="status" className="animate-fade-up rounded-full bg-ink px-4 py-2 font-ui text-[13px] text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.35)]">
            {actionMessage}
          </p>
        </div>
      )}

      {/* Library picker (phones and tablets) */}
      <Dialog
        open={scopeSheetOpen}
        onClose={() => setScopeSheetOpen(false)}
        labelledBy="wiki-scope-title"
        handle
        className="h-[72dvh] sm:h-[70vh] sm:max-w-sm"
      >
        <DialogHeader id="wiki-scope-title" title="Library" onClose={() => setScopeSheetOpen(false)} className="pb-3" />
        <EntityTreeSidebar
          mode="wiki"
          variant="plain"
          selection={selection}
          onSelect={s => { selectScope(s); setScopeSheetOpen(false); }}
          entityCounts={entityCounts}
          totalCount={totalWikiCount}
          className="flex-1"
        />
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        labelledBy="wiki-delete-title"
        className="sm:max-w-sm"
      >
        <DialogHeader id="wiki-delete-title" title="Delete page?" />
        <DialogBody>
          <p className="text-[13px] text-neutral-500">
            “<span className="text-ink">{deleteTarget?.title}</span>” will be deleted permanently.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="primary" onClick={handleDelete} loading={deleting}>Delete</Button>
        </DialogFooter>
      </Dialog>

      {/* Preview */}
      <Dialog
        open={!!previewPage}
        onClose={() => setPreviewPage(null)}
        labelledBy="wiki-preview-title"
        handle
        className="max-h-[88dvh] sm:max-h-[84vh] sm:max-w-2xl"
      >
        {previewPage && (
          <>
            <DialogHeader
              id="wiki-preview-title"
              title={previewPage.title}
              eyebrow={[previewPage.entityName, `v${previewPage.version}`, WIKI_STATUS[previewPage.status]?.label].filter(Boolean).join(' · ')}
              onClose={() => setPreviewPage(null)}
            />
            <DialogBody>
              {sanitized ? (
                <div className="wiki-article" dangerouslySetInnerHTML={{ __html: sanitized }} />
              ) : (
                <p className="py-6 text-center text-[13px] text-neutral-400">No content yet</p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => { setPreviewPage(null); navigate(`/wiki/${previewPage.id}/edit`); }}>
                <Pencil className="h-4 w-4" strokeWidth={1.75} />
                Edit
              </Button>
              <Button variant="dark" onClick={() => { setPreviewPage(null); navigate(`/wiki/${previewPage.id}`); }}>
                Open
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
};
